import React, { useCallback, useEffect, useState } from 'react';
import { Search, Upload, Check, Image as ImageIcon, Folder, FolderPlus, FolderInput, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';

export const ASPECTS = [['original', 'Original'], ['16:9', 'Wide 16:9'], ['4:3', 'Standard 4:3'], ['1:1', 'Square'], ['3:4', 'Portrait 3:4'], ['21:9', 'Banner 21:9']];
const FOCALS = ['left top', 'center top', 'right top', 'left center', 'center center', 'right center', 'left bottom', 'center bottom', 'right bottom'];

// Media library picker with folders + crop presets. Reuses the existing cms_media library.
export const MediaPicker = ({ api, open, onClose, onSelect, initial }) => {
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState({ folders: [], unfiled: 0, total: 0 });
  const [folder, setFolder] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [alt, setAlt] = useState('');
  const [aspect, setAspect] = useState(initial?.aspect || 'original');
  const [focal, setFocal] = useState(initial?.focal || 'center center');

  const loadFolders = useCallback(() => api.mediaFolders().then(setFolders).catch(() => {}), [api]);
  const load = useCallback(() => {
    setLoading(true);
    api.mediaList({ folder: folder || undefined, q: q || undefined }).then(setMedia).catch(() => toast.error('Could not load media')).finally(() => setLoading(false));
  }, [api, folder, q]);

  useEffect(() => { if (open) { loadFolders(); load(); } }, [open, load, loadFolders]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'pages');
      const res = await api.uploadMedia(fd);
      const item = res.media || res;
      if (folder && folder !== '__unfiled__' && item.id) await api.moveMedia(item.id, folder);
      toast.success('Uploaded');
      load(); loadFolders();
      setSelected({ ...item, folder: folder && folder !== '__unfiled__' ? folder : null });
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); } finally { setUploading(false); }
  };

  const newFolder = async () => {
    const name = window.prompt('Folder name (e.g. Hero images, Logos, Projects)');
    if (!name) return;
    await api.createMediaFolder(name); loadFolders(); setFolder(name);
  };
  const moveTo = async (dest) => {
    if (!selected?.id) return;
    await api.moveMedia(selected.id, dest || null); toast.success(dest ? `Moved to ${dest}` : 'Moved to Unfiled'); load(); loadFolders();
  };
  const removeFolder = async (name) => {
    if (!window.confirm(`Remove folder “${name}”? Images stay in the library as unfiled.`)) return;
    await api.deleteMediaFolder(name); setFolder(''); loadFolders(); load();
  };

  const confirm = async () => {
    if (selected?.id && alt !== (selected.alt_text || '')) api.updateMediaAlt(selected.id, alt).catch(() => {});
    onSelect({ url: selected.url, alt_text: alt, media_id: selected.id, aspect: aspect === 'original' ? undefined : aspect, focal: focal === 'center center' ? undefined : focal });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[88vh] flex flex-col" data-testid="media-picker">
        <DialogHeader><DialogTitle>Choose an image</DialogTitle></DialogHeader>
        <div className="flex gap-4 flex-1 min-h-0">
          <aside className="w-48 flex-shrink-0 border-r border-neutral-200 pr-3 overflow-y-auto text-sm" data-testid="media-folders">
            <div className="flex items-center justify-between mb-2"><span className="text-[11px] uppercase tracking-wide text-neutral-500">Folders</span><button onClick={newFolder} title="New folder" className="p-1 hover:bg-neutral-100" data-testid="media-new-folder"><FolderPlus className="w-4 h-4" /></button></div>
            {[['', `All media`, folders.total], ['__unfiled__', 'Unfiled', folders.unfiled], ...folders.folders.map((f) => [f.name, f.name, f.count])].map(([id, label, count]) => (
              <div key={id} className={`group flex items-center justify-between px-2 py-1.5 cursor-pointer ${folder === id ? 'bg-amber-50 text-amber-900 font-medium' : 'hover:bg-neutral-50'}`} onClick={() => setFolder(id)} data-testid={`media-folder-${id || 'all'}`}>
                <span className="flex items-center gap-2 truncate"><Folder className="w-3.5 h-3.5 flex-shrink-0" />{label}</span>
                <span className="flex items-center gap-1 text-xs text-neutral-400">{count}{id && id !== '__unfiled__' && <button onClick={(e) => { e.stopPropagation(); removeFolder(id); }} className="hidden group-hover:block p-0.5 text-red-500"><Trash2 className="w-3 h-3" /></button>}</span>
              </div>
            ))}
          </aside>
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center gap-3 mb-3">
              <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-3 text-neutral-400" /><Input placeholder="Search by name, alt text or tag…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="media-search" /></div>
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 text-sm cursor-pointer hover:bg-neutral-50"><Upload className="w-4 h-4" />{uploading ? 'Uploading…' : 'Upload'}<input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} data-testid="media-upload-input" /></label>
            </div>
            <div className="flex-1 overflow-y-auto min-h-[260px]">
              {loading ? <p className="text-sm text-neutral-500 p-4">Loading…</p> : media.length === 0 ? (
                <div className="text-center py-16 text-neutral-400"><ImageIcon className="w-10 h-10 mx-auto mb-3" /><p className="text-sm">No media here yet</p></div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 p-1">
                  {media.map((m) => (
                    <button key={m.id || m.url} onClick={() => { setSelected(m); setAlt(m.alt_text || ''); }} data-testid="media-item" title={m.original_name}
                      className={`relative aspect-square overflow-hidden border-2 ${selected?.url === m.url ? 'border-amber-500' : 'border-transparent hover:border-neutral-300'}`}>
                      <img src={m.thumbnail_url || m.url} alt={m.alt_text || m.original_name} className="w-full h-full object-cover" loading="lazy" />
                      {m.folder && <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 truncate max-w-[90%]">{m.folder}</span>}
                      {selected?.url === m.url && <span className="absolute top-1 right-1 bg-amber-500 text-white rounded-full p-0.5"><Check className="w-3 h-3" /></span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {selected && (
            <aside className="w-64 flex-shrink-0 border-l border-neutral-200 pl-4 overflow-y-auto space-y-4" data-testid="media-details">
              <div className="aspect-[4/3] bg-neutral-100 overflow-hidden"><img src={selected.url} alt="" className="w-full h-full object-cover" style={{ objectPosition: focal, aspectRatio: aspect !== 'original' ? aspect.replace(':', '/') : undefined }} /></div>
              <div><label className="text-[11px] uppercase tracking-wide text-neutral-500">Alt text</label><Input value={alt} onChange={(e) => setAlt(e.target.value)} placeholder="Describe the image" className="h-9" data-testid="media-alt-input" /></div>
              <div><label className="text-[11px] uppercase tracking-wide text-neutral-500">Crop preset</label>
                <div className="mt-1 flex flex-wrap gap-1.5">{ASPECTS.map(([id, l]) => <button key={id} onClick={() => setAspect(id)} className={`px-2 py-1 text-[11px] border ${aspect === id ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300'}`} data-testid={`media-aspect-${id}`}>{l}</button>)}</div></div>
              <div><label className="text-[11px] uppercase tracking-wide text-neutral-500">Focal point</label>
                <div className="mt-1 grid grid-cols-3 gap-1 w-24">{FOCALS.map((f) => <button key={f} onClick={() => setFocal(f)} title={f} className={`h-7 border ${focal === f ? 'bg-amber-500 border-amber-500' : 'border-neutral-300 hover:bg-neutral-100'}`} data-testid={`media-focal-${f.replace(' ', '-')}`} />)}</div></div>
              {selected.id && <div><label className="text-[11px] uppercase tracking-wide text-neutral-500 flex items-center gap-1"><FolderInput className="w-3 h-3" />Move to folder</label>
                <select className="mt-1 w-full border border-neutral-300 h-9 text-sm bg-white px-2" value={selected.folder || ''} onChange={(e) => { setSelected({ ...selected, folder: e.target.value || null }); moveTo(e.target.value); }} data-testid="media-move-select">
                  <option value="">Unfiled</option>{folders.folders.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
                </select></div>}
            </aside>
          )}
        </div>
        <div className="flex justify-end gap-3 border-t pt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!selected} onClick={confirm} data-testid="media-select-btn">Use image</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
